import type { TaskTypeDisplaySource, TaskTypeLabelSource } from './task-type-labels.type';

/**
 * Resolves the work-item type text to display for a task: the verbatim
 * external-system label (`raw_type_label`, e.g. "User Story") when present,
 * otherwise the normalized lowercased `raw_type` (e.g. "user story").
 *
 * `raw_type_label` is nullable by contract — Linear-sourced tasks never have
 * one, and tasks not yet re-synced since the field was introduced serialize
 * it as null — so the fallback is mandatory. Empty and whitespace-only labels
 * are treated as missing (and returned labels are trimmed). Display only:
 * filtering, grouping, sorting, and comparisons must keep using the
 * normalized `raw_type`.
 *
 * @param {TaskTypeLabelSource} task - Any task-like object exposing `raw_type` and `raw_type_label`
 * @returns {string | null} The display label, or null when neither field has a non-blank value
 */
const resolveTaskTypeLabel = (task: TaskTypeLabelSource): string | null => {
  const verbatimLabel = task.raw_type_label?.trim();
  return verbatimLabel || task.raw_type?.trim() || null;
};

/**
 * Resolves the work-item type text to display for a task across ALL types:
 * the verbatim external label first (`raw_type_label`), then the stored
 * `raw_type`, then the normalized `type` enum value, then null. Unlike
 * consulting `resolveTaskTypeLabel` only for UNKNOWN-typed tasks, this
 * upgrades canonical-typed tasks (e.g. type "story" synced from "User
 * Story") to their verbatim casing while rendering label-less tasks the
 * same as before. Display only: filtering, grouping, sorting, and
 * comparisons must keep using the normalized `raw_type` / `type`.
 *
 * @param {TaskTypeDisplaySource} task - Any task-like object exposing `type`, `raw_type`, and `raw_type_label`
 * @returns {string | null} The display text, or null when no field has a non-blank value
 */
const resolveTaskTypeDisplayText = (task: TaskTypeDisplaySource): string | null => {
  return resolveTaskTypeLabel(task) || task.type || null;
};

export { resolveTaskTypeDisplayText, resolveTaskTypeLabel };
